/**
 * ゲーム配信の独自ドメイン（play.mygame.com）の画面の中身のうち、React に依存しない部分（2026-09-27 creator/custom-domains）。
 * 画面は components/HostingDomains.tsx、サーバーは apps/server/src/modules/hosting/domains.ts（README の「独自ドメイン」）。
 *
 * WHY 別ファイルか: node --test（型を剥がすだけ）で確かめられるように、JSX と import.meta.env を持ち込まない。
 */

/** GET / POST …/hosting/domains の 1 件（compute/domains.ts の view = Functions の独自ドメインと同じ形） */
export interface HostingDomain {
  hostname: string;
  status: 'pending' | 'active' | 'failed';
  sslStatus: string | null;
  /**
   * 置く DNS（置く順）。CNAME（<hostname> → Kumo の CNAME 先）→ 持ち主の証明の TXT（`_kumo-verify.<hostname>` = `kumo-verify=<アカウントごとの値>`）
   * → Cloudflare が求めたときだけ TXT が続く。
   * 2026-09-27 Core の統合⑰（SEC-R3-07 domains-sec）で持ち主の TXT が入った: 以前は先に登録した人が名前を押さえられたが、
   * 今は TXT で持ち主を証明できた方が取る。TXT は active の後も 1 日 1 回確かめ直す（消すと配信が止まる）
   */
  dns: Array<{ type: string; name: string; value: string }>;
  /** 持ち主の証明ができて Cloudflare に登録済みか（統合⑰）。古い応答には無い = 表示しない */
  verified?: boolean;
  /** 最後に TXT を確かめた時刻（UTC の ISO・まだなら null） */
  verifiedAt?: string | null;
  /** 次にやること（サーバーの英文。例: 「TXT を置いてください」）。画面はそのまま出す */
  errors: string[];
  createdAt?: string;
  checkedAt?: string | null;
  activatedAt?: string | null;
  /**
   * パスごとのアプリ（2026-10-06 compute/domain-paths・サーバーの domainMounts.ts）。1 つのドメインの下で、パスごとに別のアプリを出せる
   * （mygame.com/race → A・mygame.com/ → B。並べられるのは同じアカウントのアプリだけ）。
   * path = このアプリのパス（"/" = ドメインの根元）・url = このアプリの URL（https://mygame.com/race/）・otherPaths = 同じドメインの他のアプリのパス。
   * 古い応答には無い = 根元として扱う（appUrl / targetLabel）
   */
  path?: string;
  url?: string;
  otherPaths?: string[];
  /** POST の応答だけ。パスの下で壊れそうな所（/ で始まる参照など）。message はサーバーの英文 = 画面はそのまま出す */
  warnings?: MountWarning[];
}

/** 足したときの警告（サーバーの mountWarnings.ts。Kumo はファイルを書き換えない = 場所を知らせて作者が直す） */
export interface MountWarning {
  code: string;
  message: string;
  path?: string;
  line?: number;
  ref?: string;
  count?: number;
}

/**
 * コピー用の DNS の行（`TYPE  NAME  VALUE`・1 レコード 1 行・列をそろえる）。CLI の `xkumo hosting domains add` と同じ形
 * （packages/cli/src/commands/hosting.ts の dnsLines）= 画面と端末で見え方が同じ。ブラウザ向けの別パッケージなので import はしない
 */
export function dnsText(records: HostingDomain['dns']): string {
  const typeW = Math.max(0, ...records.map((r) => r.type.length));
  const nameW = Math.max(0, ...records.map((r) => r.name.length));
  return records.map((r) => `${r.type.padEnd(typeW)}  ${r.name.padEnd(nameW)}  ${r.value}`).join('\n');
}

/** 持ち主の証明の TXT の名前の頭（サーバーの compute/domainProof.ts の VERIFY_LABEL と同じ） */
export const VERIFY_LABEL = '_kumo-verify';

/**
 * 親のドメインに置く TXT の名前（`play.mygame.com` → `_kumo-verify.mygame.com`）。すでに 2 ラベル（apex）なら null（それ自体が親）。
 * WHY 出すか: サーバーは親のドメインの TXT も見る（domainProof.ts）= 1 つ置けばその下の名前（play.・www.・beta.）は全部通る。
 *   ドメインを何個も足す人が、名前ごとに TXT を置いて回らなくて済む。公開サフィックス（co.uk 等）は見ない（サーバーの文言の例と同じ）
 */
export function parentVerifyName(hostname: string): string | null {
  const labels = hostname.replace(/\.+$/, '').split('.');
  if (labels.length <= 2) return null;
  return `${VERIFY_LABEL}.${labels.slice(-2).join('.')}`;
}

/** apex（mygame.com のような 2 ラベル）。CNAME を置けない DNS がある。公開サフィックス（co.uk 等）までは見ない = 外れても注記が 1 つ増えるだけ */
export const isApex = (hostname: string) => hostname.replace(/\.+$/, '').split('.').length === 2;

/**
 * 入力の整形（https:// や末尾の / を貼られても通す）。形の検査はサーバー（normalizeHostname）に任せる = 規則を 2 か所に持たない。
 * WHY 貼られた URL を直すか: ブラウザのアドレス欄からコピーすると `https://play.mygame.com/` になる。そのまま送ると 400 で、直し方が分かりにくい
 */
export function cleanHostname(input: string): string {
  return input
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/[/?#].*$/, '')
    .replace(/\.+$/, '')
    .toLowerCase();
}

/** 削除の確認で打たせた名前が一致するか（大文字・末尾のドットの違いは同じとみなす） */
export const sameHostname = (a: string, b: string) => cleanHostname(a) === cleanHostname(b) && cleanHostname(a) !== '';

/**
 * パスの欄の整形（2026-10-06 compute/domain-paths）。空 = ドメインの根元 "/"。`race`・`/race/` は `/race` に（サーバーの normalizeMountPath と同じ直し方）。
 * 形の検査（小文字の英数字と - _・ドット不可）はサーバーに任せる = 規則を 2 か所に持たない。
 * WHY 大文字を小文字にしないか: URL のパスは大文字と小文字を区別する。黙って直すと打った URL と違う所に出る → サーバーの 400 の文で直してもらう
 */
export function cleanPath(input: string): string {
  const p = input.trim().replace(/[?#].*$/, '').replace(/^\/+/, '').replace(/\/+$/, '');
  return p ? `/${p}` : '/';
}

/**
 * 足す先（ホスト名とパス）。パスの欄が空なら、ドメインの欄に書いたパス（`mygame.com/race`・`https://mygame.com/race/`）を使う。
 * WHY: サーバーと CLI は `mygame.com/race` の 1 語を受ける = 画面だけ黙って根元に足すと、打った人の思った所と違う。
 *   ホスト名とパスを分けて送る（サーバーは hostname に / があって path もあると 400 = どちらが正しいか分からないため）。
 * 末尾のファイル名（`/index.html`）は捨てる: アドレス欄から貼った URL は今までどおり通したい（パスにドットは使えない = ファイル名と分かる）
 */
export function addTarget(hostInput: string, pathInput: string): { hostname: string; path: string } {
  const rest = hostInput
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^[^/?#]*/, '')
    .replace(/[?#].*$/, '');
  const fromHost = rest.replace(/\/[^/]*\.[^/]*$/, '');
  return { hostname: cleanHostname(hostInput), path: pathInput.trim() ? cleanPath(pathInput) : cleanPath(fromHost) };
}

/** 画面に出す「どこに出るか」（根元 = mygame.com・パス = mygame.com/race）。確認の画面（サーバーの describe）と同じ書き方 */
export const targetLabel = (hostname: string, path: string | undefined) => (path && path !== '/' ? `${hostname}${path}` : hostname);

/** このアプリの URL。サーバーの url が先（古い応答には無い = ドメインの根元） */
export const appUrl = (d: Pick<HostingDomain, 'hostname' | 'path' | 'url'>) => d.url ?? `https://${targetLabel(d.hostname, d.path)}/`;

/**
 * 外したときにドメインが残るか（同じドメインに他のアプリがいる）。サーバーの DELETE は「このアプリの割り当て」だけを外し、
 * 最後の 1 つならドメインごと消す（= 他の人が登録できるようになる）。確認の文をこれで変える
 */
export const removeKeepsDomain = (d: Pick<HostingDomain, 'otherPaths'>) => (d.otherPaths?.length ?? 0) > 0;

/** 1 ドメインの月額（サーバーの compute/domainPricing.ts。一覧の応答の pricing・単価の行が無ければ null） */
export interface DomainPricing {
  monthlyUsd: number;
  per: 'domain';
  proration: 'daily';
}

/**
 * 料金の 1 行の文のキーと差し込み（2026-10-06 creator/domain-words）。
 * WHY: 以前の「Custom domains are billed daily at cost」は、ドメインそのものの代金を KUMODeck が取るように読めた
 *   （founder「ドメイン管理を KUMO でやるわけじゃないのに意味がわからん」）。実際は 1 ドメイン月 $0.10（Cloudflare for SaaS の証明書と配信）だけ（2026-10-07 money/cf-recon 追記 7 から Cloudflare の請求書の税を含む $0.11 がサーバーの pricing で来る）。
 *   値段はサーバーの pricing（単価表 0250）から入れる = 画面に数字を写さない。古いサーバー（pricing 無し）は数字なしの同じ言い方
 */
export function domainCostMessage(pricing: DomainPricing | null | undefined): { key: 'domains.cost' | 'domains.costUnpriced'; vars: Record<string, string> } {
  if (!pricing) return { key: 'domains.costUnpriced', vars: {} };
  return { key: 'domains.cost', vars: { price: `$${pricing.monthlyUsd.toFixed(2)}` } };
}
